import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Popover,
  TextField,
} from "@mui/material";
import { GameClockInput } from "./GameClockInput";
import { TimeoutsLeftIndicator } from "./TimeoutsLeftIndicator";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import EditIcon from "@mui/icons-material/Edit";
import PlaylistAddCheckSharpIcon from "@mui/icons-material/PlaylistAddCheckSharp";
import { useLogger } from "../../../../contexts/LoggerContext";
import { YardlineInput } from "./YardlineInput";

interface Props {
  handleLogTableToggle: () => void;
  handleInGameStatsToggle: () => void;
  handleSavePlayDialogOpen: () => void;
  handleEditScoreboard: () => void;
}
export const Scoreboard = ({
  handleLogTableToggle,
  handleInGameStatsToggle,
  handleSavePlayDialogOpen,
  handleEditScoreboard,
}: Props) => {
  const { selectedGame, gameSituation, setGameSituation } = useLogger();
  console.log(selectedGame)
  const clubLogo = selectedGame?.user_club?.profile_picture;
  const oppClubLogo = selectedGame?.opponent_club?.profile_picture;
  const clubName = selectedGame?.user_club?.name;
  const oppClubName = selectedGame?.opponent_club?.name;
  console.log(clubLogo)

  // State for managing popovers
  const [anchorEls, setAnchorEls] = useState<{
    quarter: HTMLElement | null;
    time: HTMLElement | null;
    ballOn: HTMLElement | null;
    down: HTMLElement | null;
    distance: HTMLElement | null;
  }>({
    quarter: null,
    time: null,
    ballOn: null,
    down: null,
    distance: null,
  });

  // State for temporary values while editing
  const [tempValues, setTempValues] = useState<{
    quarter: number;
    down: number;
    distance: number;
  }>({
    quarter: gameSituation.quarter,
    down: gameSituation.down,
    distance: gameSituation.distance,
  });

  // Click handlers for each field
  const handleClick =
    (field: keyof typeof anchorEls) =>
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
    };

  const handleClose = (field: keyof typeof anchorEls) => () => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        px: { xs: 1, sm: 2 },
        py: 1,
        gap: { xs: 1, sm: 2 },
        borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
      }}
    >
      {/* User Club Logo and Name (side by side) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 30,
          gap: 1,
        }}
      >
        {clubLogo && (
          <Box
            component="img"
            src={clubLogo}
            alt={clubName}
            sx={{
              height: 40,
              width: 40,
              objectFit: "contain",
              borderRadius: "4px",
              ml: { xs: 0, sm: 0.5 },
              mr: 0.5,
            }}
          />
        )}
      </Box>

      {/* User Club Timeouts (vertical) and Score (full height) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 32,
          mr: 0.0,
          gap: 0.5,
        }}
      >
        <TimeoutsLeftIndicator timeoutsLeft={gameSituation.club_tol} size={8} />
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{
            lineHeight: 1,
            minWidth: 28,
            textAlign: "center",
            mr: 0.25,
          }}
        >
          {gameSituation.club_score}
        </Typography>
        {/* Football icon if user club has possession */}
        {gameSituation.club_possession && (
          <SportsFootballIcon
            sx={{
              color: "gold",
              fontSize: 20,
              ml: 0.25,
              mr: 0,
              verticalAlign: "middle",
            }}
          />
        )}
      </Box>

      {/* VS */}
      <Typography variant="h6" sx={{ color: "grey.400" }}>
        VS
      </Typography>

      {/* Opponent Club Score (full height) and Timeouts (vertical) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 32,
          ml: 0.0,
          gap: 0.5,
        }}
      >
        {/* Football icon if opponent club has possession */}
        {!gameSituation.club_possession && (
          <SportsFootballIcon
            sx={{
              color: "gold",
              fontSize: 20,
              mr: 0.5,
              ml: 0,
              verticalAlign: "middle",
            }}
          />
        )}
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{ lineHeight: 1, minWidth: 28, textAlign: "center" }}
        >
          {gameSituation.opp_score}
        </Typography>
        <TimeoutsLeftIndicator timeoutsLeft={gameSituation.opp_tol} size={8} />
      </Box>

      {/* Opponent Club Logo and Name (side by side) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 30,
          gap: 1,
          pr: 0,
        }}
      >
        {oppClubLogo && (
          <Box
            component="img"
            src={oppClubLogo}
            alt={oppClubName}
            sx={{
              height: 40,
              width: 40,
              objectFit: "contain",
              borderRadius: "4px",
              mr: 0,
              ml: 0.5,
            }}
          />
        )}
      </Box>

      {/* Vertical Divider */}
      <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />

      {/* Game Situation Info: Single row, each stat is a column with label and value stacked */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          minWidth: 420,
        }}
      >
        {/* Quarter */}
        <Box
          onClick={handleClick("quarter")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography variant="body1">Qtr</Typography>
          <Typography variant="h6" fontWeight={500}>
            {gameSituation.quarter === -1 ? "-" : gameSituation.quarter}
          </Typography>
        </Box>
        {/* Time */}
        <Box
          onClick={handleClick("time")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography variant="body1">Time</Typography>
          <Typography variant="h6" fontWeight={500}>
            {gameSituation.time_remaining}
          </Typography>
        </Box>
        {/* Ball On */}
        <Box
          onClick={handleClick("ballOn")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="body1"
            sx={{ whiteSpace: "nowrap", minWidth: 60 }}
          >
            Ball On
          </Typography>
          <Typography variant="h6" fontWeight={500}>
            {gameSituation.yardline}
          </Typography>
        </Box>
        {/* Down */}
        <Box
          onClick={handleClick("down")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography variant="body1">Down</Typography>
          <Typography variant="h6" fontWeight={500}>
            {gameSituation.down === -1 ? "-" : gameSituation.down}
          </Typography>
        </Box>
        {/* Distance */}
        <Box
          onClick={handleClick("distance")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography variant="body1">Distance</Typography>
          <Typography variant="h6" fontWeight={500}>
            {gameSituation.distance === -1 ? "-" : gameSituation.distance}
          </Typography>
        </Box>

        {/* Popovers */}
        {/* Quarter */}
        <Popover
          open={Boolean(anchorEls.quarter)}
          anchorEl={anchorEls.quarter}
          onClose={handleClose("quarter")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="subtitle1">Quarter</Typography>
            <TextField
              type="number"
              value={gameSituation.quarter === -1 ? "" : gameSituation.quarter}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 4) {
                  setGameSituation({
                    ...gameSituation,
                    quarter: value,
                  });
                }
              }}
              inputProps={{
                min: 1,
                max: 4,
                step: 1,
              }}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
        </Popover>

        {/* Time */}
        <Popover
          open={Boolean(anchorEls.time)}
          anchorEl={anchorEls.time}
          onClose={handleClose("time")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            sx: { width: 300 },
          }}
        >
          <GameClockInput />
        </Popover>

        {/* Ball On */}
        <Popover
          open={Boolean(anchorEls.ballOn)}
          anchorEl={anchorEls.ballOn}
          onClose={handleClose("ballOn")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            sx: { width: 250, px: 2 },
          }}
        >
          <Box sx={{ py: 3 }}>
            <YardlineInput
              value={gameSituation.yardline}
              onChange={(value) => {
                setGameSituation({
                  ...gameSituation,
                  yardline: value,
                });
              }}
              label="Ball On"
            />
          </Box>
        </Popover>

        {/* Down */}
        <Popover
          open={Boolean(anchorEls.down)}
          anchorEl={anchorEls.down}
          onClose={handleClose("down")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="subtitle1">Down</Typography>
            <TextField
              type="number"
              value={gameSituation.down === -1 ? "" : gameSituation.down}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 4) {
                  setGameSituation({
                    ...gameSituation,
                    down: value,
                  });
                }
              }}
              inputProps={{
                min: 1,
                max: 4,
                step: 1,
              }}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
        </Popover>

        {/* Distance */}
        <Popover
          open={Boolean(anchorEls.distance)}
          anchorEl={anchorEls.distance}
          onClose={handleClose("distance")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <YardlineInput
              value={gameSituation.distance}
              onChange={(value) => {
                setGameSituation({
                  ...gameSituation,
                  distance: value,
                });
              }}
              label="Distance"
            />
          </Box>
        </Popover>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
      {/* icons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        {/* Adjust Scoreboard state and SavePlay */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 0.25,
            gap: 1.0,
            minWidth: 32,
          }}
        >
          <Tooltip title="Edit Scoreboard">
            <IconButton onClick={handleEditScoreboard}>
              <EditIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save Play">
            <IconButton onClick={handleSavePlayDialogOpen}>
              <PlaylistAddCheckSharpIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Logs and Stats */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minWidth: 32,
          }}
        >
          <Tooltip title="Game Logs">
            <IconButton onClick={handleLogTableToggle}>
              <AssignmentIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Tooltip title="In-Game Stats">
            <IconButton onClick={handleInGameStatsToggle}>
              <BarChartIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
